import { inject, Injectable, Injector, NgZone } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { App } from '@capacitor/app';
import { StatusBar } from '@capacitor/status-bar';
import { Platform } from '@ionic/angular/standalone';
import { NotificationClickEvent } from 'onesignal-cordova-plugin';
import { firstValueFrom } from 'rxjs';
import { filter, first, switchMap } from 'rxjs/operators';
import { authLog } from 'src/app/auth/auth.handler';
import { NotificationType } from 'src/app/contracts/api.types';
import { AppSettings } from 'src/app/core/common/app.settings';
import { Auth } from 'src/app/core/common/auth';
import { Back } from 'src/app/core/common/back';
import { Configs } from 'src/app/core/common/configs';
import { ErrorTracker } from 'src/app/core/common/error.tracker';
import { Push } from 'src/app/core/common/push';
import { backButton$, IPushNotificationDetails, Logs } from 'src/app/core/contracts';
import { log } from 'src/app/core/helpers/logger';
import { UpdateHandler } from 'src/app/core/helpers/update.handler';
import { EvaluationData } from 'src/app/data/evaluation.data';
import { AdjustService } from 'src/app/services/adjust.service';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { GenesysService } from 'src/app/services/genesys.service';
import { MarketingService } from 'src/app/services/marketing.service';
import { SocialMediaService } from 'src/app/services/social-media.service';

@Injectable()
export class Initializer {
  private platform = inject(Platform);
  private settings = inject(AppSettings);
  private push = inject(Push);
  private injector = inject(Injector);
  private analytics = inject(AnalyticsService);
  private adjust = inject(AdjustService);
  private genesys = inject(GenesysService);
  private socialMedia = inject(SocialMediaService);
  marketing = inject(MarketingService);
  public async init() {
    await this.platform.ready();
    if (this.platform.is('capacitor') && !this.platform.is('ios')) {
      await StatusBar.setOverlaysWebView({ overlay: false });
    }

    this.analytics.init();
    this.adjust.init();
    this.genesys.init();
    this.socialMedia.init();

    const router = this.injector.get(Router);
    const auth = this.injector.get(Auth);

    this.handleBackButton(router);
    this.setupNotifications(router, auth);

    return Promise.all([
      auth.checkAuth().then(() => {
        authLog(`auth.flow::=>initializer.auth.checkAuth: completed`);
      }),
      this.settings.initLanguage().then(() => {
        authLog(`auth.flow::=>initializer.settings.initLanguage: completed`);
      }),
    ])
      .then(async () => {
        authLog(`auth.flow::=>initializer: completed auth & settings initialization`);

        const configs = this.injector.get(Configs);
        const loadedConfigs = await firstValueFrom(configs.loadConfigs());

        const updateHandler = this.injector.get(UpdateHandler);
        updateHandler.checkForUpdates(loadedConfigs?.updateCheck);

        const tracker = this.injector.get(ErrorTracker);
        tracker.init();
      })
      .then(() => {
        authLog(`auth.flow::=>initializer.init: completed`);
      });
  }
  private handleBackButton(router: Router) {
    const back = this.injector.get(Back);
    this.platform.backButton.subscribeWithPriority(5, async processNextHandler => {
      if (backButton$.observed) {
        log('@handleBackButton: forward to backButton$');
        backButton$.next();
      } else {
        const { exit, canGoBack, redirectTo } = await back.canBackWithPhysicalButton(router.url);
        if (Logs.ENABLE_BACK_NAVIGATION_LOGS) {
          log(`@canGoBack result: canGoBack: ${canGoBack}, exit: ${exit}, redirectTo: ${redirectTo?.toString()}`);
        }

        if (exit) {
          App.exitApp();
          return;
        }
        if (redirectTo) {
          router.navigate([redirectTo]);
          return;
        }
        if (canGoBack) {
          processNextHandler();
        }
      }
    });
  }
  private setupNotifications(router: Router, auth: Auth) {
    const onNotificationOpened = (ev: NotificationClickEvent) => {
      const details = ev.notification.additionalData as IPushNotificationDetails;
      log(`@push.opened: type=${details?.type}, data=${JSON.stringify(details)}`);
      if (!details) {
        return;
      }
      const zone = this.injector.get(NgZone);
      let action: () => void;
      switch (details.type) {
        case NotificationType.ResourceArrived:
        case NotificationType.ResourceOnTheWay:
        case NotificationType.VisitStarted:
        case NotificationType.VisitCompleted:
          action = () => {
            zone.run(() => {
              // run inside angular zone otherwise hourly-contracts/details will be blank
              router.navigate([`hourly/details/${details.bookingId}`]);
            });
          };
          break;
        case NotificationType.MonthlyContractRenewReminder:
          action = () => {
            zone.run(() => {
              router.navigate([`monthly/details/${details.requestId}`]);
            });
          };
          break;
        case NotificationType.EvaluateHourlyVisitEnded:
        case NotificationType.EvaluateHourlyContractEnded:
        case NotificationType.EvaluateHourlyContractVisitEnded:
        case NotificationType.EvaluateMonthlyContractEnded:
        case NotificationType.EvaluateMonthlyContractFreezed:
          action = () => {
            zone.run(() => {
              const evaluationData = this.injector.get(EvaluationData);
              evaluationData.evaluationOpenedFromNotification = true;
              log(`@push.evaluation: userEvaluationId=${details.userEvaluationId}`);
              evaluationData.openEvaluation(evaluationData.getUserEvaluation(details.userEvaluationId as number)).subscribe();
            });
          };
          break;
        default:
          action = () => {};
      }
      if (router.url === '/') {
        // the app was closed
        router.events
          .pipe(
            filter(evt => evt instanceof NavigationEnd),
            first(),
            switchMap(() =>
              auth.isAuthenticated$.pipe(
                filter(isAuthenticated => isAuthenticated),
                first()
              )
            )
          )
          .subscribe(action);
      } else {
        // the app is in the background/foreground
        // if router.url === '/location'  location.back()
        action();
      }
    };
    this.push.init(onNotificationOpened);
  }
}
